// src/App.tsx
import React, { useEffect, useSyncExternalStore } from 'react';
import { Provider } from 'react-redux';
import { store } from '@app/store';
import AppRouter, { router } from '@app/router';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { setOnline } from '@app/store/slices/uiSlice';
import fetchAndSetUserInfo, { refreshUserInfo } from '@shared/services/userService';
import ErrorBoundary from '@shared/components/ErrorBoundary';
import SystemLogsViewer from '@shared/components/SystemLogsViewer';
import BugsViewer from '@shared/components/BugsViewer';
import FeatureRequestsViewer from '@shared/components/FeatureRequestsViewer';
import './App.css';
import { useUserPresence } from '@core/hooks/useUserPresence';

type RuntimeFlags = Record<string, string | boolean | undefined>;
const getRuntimeFlags = (): RuntimeFlags => (globalThis as typeof globalThis & { __SODOR_DEBUG_FLAGS__?: RuntimeFlags }).__SODOR_DEBUG_FLAGS__ ?? {};
const subscribeToRouter = (onChange: () => void) => router.subscribe(onChange);
const getCurrentPath = () => router.state.location.pathname;

/** In dev: enabled unless VITE_DEBUG_LOGS_ENABLED=false|0. In production: enabled only if VITE_DEBUG_LOGS_ENABLED=true|1 (set at build time). */
function isDebugLogsEnabled(): boolean {
  const v = getRuntimeFlags().VITE_DEBUG_LOGS_ENABLED;
  // In dev mode, default to true unless explicitly disabled
  if (getRuntimeFlags().dev === true) {
    return v !== 'false' && v !== '0' && v !== false;
  }
  // In production, only if explicitly enabled
  return v === 'true' || v === '1' || v === true;
}

/** In dev: enabled unless VITE_DEBUG_BUGS_ENABLED=false|0. In production: enabled only if VITE_DEBUG_BUGS_ENABLED=true|1 (set at build time). */
function isDebugBugsEnabled(): boolean {
  const v = getRuntimeFlags().VITE_DEBUG_BUGS_ENABLED;
  // In dev mode, default to true unless explicitly disabled
  if (getRuntimeFlags().dev === true) {
    return v !== 'false' && v !== '0' && v !== false;
  }
  // In production, only if explicitly enabled
  return v === 'true' || v === '1' || v === true;
}

/** In dev: enabled unless VITE_DEBUG_FEATURE_REQUESTS_ENABLED=false|0. In production: enabled only if VITE_DEBUG_FEATURE_REQUESTS_ENABLED=true|1 (set at build time). */
function isDebugFeatureRequestsEnabled(): boolean {
  const v = getRuntimeFlags().VITE_DEBUG_FEATURE_REQUESTS_ENABLED;
  // In dev mode, default to true unless explicitly disabled
  if (getRuntimeFlags().dev === true) {
    return v !== 'false' && v !== '0' && v !== false;
  }
  // In production, only if explicitly enabled
  return v === 'true' || v === '1' || v === true;
}

const AppContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const { online } = useAppSelector((state) => state.ui);
  const isAuthenticated = useAppSelector((state) => state.auth?.isAuthenticated ?? false);
  const presenceToken = useAppSelector((state) => state.auth?.token ?? null);
  const pathname = useSyncExternalStore(subscribeToRouter, getCurrentPath, getCurrentPath);
  const normalizedPath = pathname.replace(/\/+$/, '') || '/';
  const isLandingPage = normalizedPath === '/' || normalizedPath === '/welcome';
  useUserPresence(isAuthenticated ? presenceToken : null);

  useEffect(() => {
    console.log('✅ App Content mounted');
    // در صفحات ورود، اطلاعات کاربر را نمی‌گیریم تا با توکن قدیمی/نامعتبر
    // درخواست ۴۰۱ ارسال نشود و کاربر به صفحه لاگین دیگر پرتاب نشود.
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const isLoginPage = path === '/login_password' || path === '/Adminlogin_pass';
    if (!isLoginPage) {
      fetchAndSetUserInfo();
    }

    // Monitor online/offline status
    const handleOnline = () => dispatch(setOnline(true));
    const handleOffline = () => dispatch(setOnline(false));

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Permissions may be changed by an administrator while this session is
    // open. Refresh them when the user returns to the app so the menu does not
    // keep using a stale localStorage snapshot.
    const refreshPermissionsOnFocus = () => {
      if (isAuthenticated) void refreshUserInfo({ background: true });
    };
    window.addEventListener('focus', refreshPermissionsOnFocus);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('focus', refreshPermissionsOnFocus);
    };
  }, [dispatch, isAuthenticated]);

  return (
    <>
      {!online && (
        <div className="text-center text-xs py-1 text-white bg-stone-900">
          اتصالتان به اینترنت را بررسی کنید
        </div>
      )}
      <AppRouter />
      <ToastContainer
        position="bottom-left"
        autoClose={15000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={true}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      {isDebugLogsEnabled() && <SystemLogsViewer />}
      {isAuthenticated && !isLandingPage && isDebugBugsEnabled() && (
        <BugsViewer
          logsEnabled={isDebugLogsEnabled()}
          featureRequestsEnabled={isDebugFeatureRequestsEnabled()}
        />
      )}
      {isAuthenticated && !isLandingPage && isDebugFeatureRequestsEnabled() && (
        <FeatureRequestsViewer
          logsEnabled={isDebugLogsEnabled()}
          bugsEnabled={isDebugBugsEnabled()}
        />
      )}
    </>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <div className="App" dir="rtl" style={{ minHeight: '100vh' }}>
          <AppContent />
        </div>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
